import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Breadcrumbs, Button, Card, CircularProgress, Container, MenuItem, Stack,
  TextField, Typography,
} from '@mui/material';
import Page from '../components/Page';
import LiveReportResults from '../components/LiveReportResults';
import LiveReportBarChart from '../components/LiveReportBarChart';
import { getLiveReportCameras, getLiveReportZones, getLiveZoneReport } from '../api/liveReports';
import { LIVE_REPORT_TIMEZONE, toLiveReportDateInput, toLiveReportTimestamp } from '../utils/liveReportDates';
import DashboardBreadcrumbLink from 'src/components/DashboardBreadcrumbLink';

export default function LiveReports() {
  const clientId = Number(localStorage.getItem('client_id'));
  const hasAccess = [1442, 1444].includes(clientId);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState('');
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState('all');
  const [interval, setInterval] = useState(() => {
    const now = new Date();
    return {
      start: toLiveReportDateInput(new Date(now.getTime() - 3600000)),
      end: toLiveReportDateInput(now),
    };
  });
  const [loadingCameras, setLoadingCameras] = useState(true);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [zoneError, setZoneError] = useState('');
  const [reportError, setReportError] = useState('');
  const [report, setReport] = useState(null);
  const [retry, setRetry] = useState(0);
  const reportRequest = useRef(0);
  const viewId = cameras.find((camera) => camera.id === cameraId)?.viewId;

  useEffect(() => {
    if (!hasAccess) {
      setLoadingCameras(false);
      return undefined;
    }
    let active = true;
    setLoadingCameras(true);
    setCameraError('');
    getLiveReportCameras(clientId).then((items) => {
      if (!active) return;
      setCameras(items);
      setCameraId((current) => items.some((camera) => camera.id === current) ? current : items[0]?.id || '');
    }).catch(() => {
      if (active) setCameraError('Unable to load cameras. Please try again.');
    }).finally(() => {
      if (active) setLoadingCameras(false);
    });
    return () => { active = false; };
  }, [clientId, hasAccess, retry]);

  useEffect(() => {
    if (!hasAccess || !cameraId) return undefined;
    let active = true;
    setLoadingZones(true);
    setZoneError('');
    setZones([]);
    setZoneId('all');
    getLiveReportZones({ clientId, cameraId, viewId }).then((items) => {
      if (active) setZones(items);
    }).catch(() => {
      if (active) setZoneError('Unable to load zones. Please try again.');
    }).finally(() => {
      if (active) setLoadingZones(false);
    });
    return () => { active = false; };
  }, [clientId, cameraId, viewId, hasAccess, retry]);

  useEffect(() => () => { reportRequest.current += 1; }, []);

  const clearReport = () => {
    reportRequest.current += 1;
    setLoadingReport(false);
    setReport(null);
    setReportError('');
  };

  const viewReport = async (event) => {
    event.preventDefault();
    const startDateTime = toLiveReportTimestamp(interval.start);
    const endDateTime = toLiveReportTimestamp(interval.end);
    if (!interval.start || !interval.end || !Number.isFinite(Date.parse(startDateTime))
      || !Number.isFinite(Date.parse(endDateTime)) || Date.parse(startDateTime) >= Date.parse(endDateTime)) {
      setReportError('End date and time must be after start date and time.');
      return;
    }
    if (!hasAccess || !cameraId || viewId === null || viewId === undefined || loadingZones || zoneError) return;
    const requestId = ++reportRequest.current;
    setLoadingReport(true);
    setReportError('');
    setReport(null);
    try {
      const result = await getLiveZoneReport({
        clientId, cameraId, viewId, zones, zoneIds: zoneId === 'all' ? [] : [zoneId],
        startDateTime, endDateTime, timezone: LIVE_REPORT_TIMEZONE,
      });
      if (requestId === reportRequest.current) setReport(result);
    } catch {
      if (requestId === reportRequest.current) setReportError('Unable to load the report. Please try again.');
    } finally {
      if (requestId === reportRequest.current) setLoadingReport(false);
    }
  };

  return (
    <Page title="Live Reports">
      <Container maxWidth="xxl" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>Reports</Typography>    
         <Breadcrumbs separator="•" aria-label="breadcrumb" sx={{ mb: 4 }}>
          <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
          <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Reports</Typography>
        </Breadcrumbs>  
        {!hasAccess ? <Alert severity="warning">Live Reports is available for clients 1442 and 1444.</Alert> : (
          <>
            <LiveReportBarChart cameras={cameras} loadingCameras={loadingCameras} />
            <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderColor: 'divider', boxShadow: 'none' }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Zone report</Typography>
              <Typography variant="body2" color="text.secondary">
                Monitoring observations are saved automatically. Choose the interval you want to review.
              </Typography>
              <Box component="form" onSubmit={viewReport} sx={{ mt: 4 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
                  <TextField select fullWidth size="small" label="Camera" value={cameraId}
                    disabled={loadingCameras || !cameras.length}
                    onChange={(event) => {
                      clearReport();
                      setZones([]);
                      setZoneId('all');
                      setLoadingZones(true);
                      setCameraId(event.target.value);
                    }}>
                    {cameras.map((camera) => <MenuItem key={camera.id} value={camera.id}>{camera.name}</MenuItem>)}
                  </TextField>
                  {['start', 'end'].map((field) => (
                    <TextField key={field} fullWidth required size="small" type="datetime-local"
                      label={`${field === 'start' ? 'Start' : 'End'} date and time`}
                      value={interval[field]} InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      sx={{ '& input': { colorScheme: (theme) => theme.palette.mode } }}
                      onChange={(event) => {
                        clearReport();
                        setInterval((current) => ({ ...current, [field]: event.target.value }));
                      }} />
                  ))}
                  <TextField select fullWidth size="small" label="Zones" value={zoneId}
                    disabled={!cameraId || loadingZones || !!zoneError}
                    onChange={(event) => { clearReport(); setZoneId(event.target.value); }}>
                    <MenuItem value="all">All zones</MenuItem>
                    {zones.map((zone) => <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>)}
                  </TextField>
                </Box>
                {(loadingCameras || loadingZones) && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }} role="status">
                    <CircularProgress size={16} /><Typography variant="body2" color="text.secondary">
                      {loadingCameras ? 'Loading cameras...' : 'Loading zones...'}
                    </Typography>
                  </Stack>
                )}
                {(cameraError || zoneError) && <Alert severity="error" sx={{ mt: 2 }}
                  action={<Button color="inherit" onClick={() => { clearReport(); setRetry((value) => value + 1); }}>Retry</Button>}>
                  {cameraError || zoneError}
                </Alert>}
                {!loadingCameras && !cameraError && !cameras.length && <Alert severity="info" sx={{ mt: 2 }}>No cameras available for this client.</Alert>}
                {!loadingCameras && !loadingZones && cameraId && !zoneError && !zones.length && <Alert severity="info" sx={{ mt: 2 }}>No zones available for this camera.</Alert>}
                {reportError && <Alert severity="error" sx={{ mt: 2 }}>{reportError}</Alert>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 4 }}>
                  <Button type="submit" variant="contained" disabled={!cameraId || viewId === null || viewId === undefined || loadingCameras || loadingZones || loadingReport || !!cameraError || !!zoneError}
                    startIcon={loadingReport ? <CircularProgress size={16} color="inherit" /> : null}>
                    {loadingReport ? 'Loading report...' : 'View report'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">Date and time: {LIVE_REPORT_TIMEZONE}</Typography>
                </Stack>
              </Box>
            </Card>
            {report && <LiveReportResults key={reportRequest.current} report={report} />}
          </>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 6 }}>
          <Typography variant="caption" color="text.secondary">VIDISKY</Typography>
          <Typography variant="caption" color="text.secondary">{LIVE_REPORT_TIMEZONE} · Observed analytics</Typography>
        </Stack>
      </Container>
    </Page>
  );
}
